import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { TOAST_API, type Toast, type ToastHandle, type ToastKind, type ToastOptions } from '@chess/api-toast';

// Provides `toast` over the host's notifications: the platform renders the
// stack in the frame and the theme styles it; what goes inside each toast,
// the icon for its kind, how long it stays, and any later handling such as
// a log of every toast, is decided here and nowhere else.
const DEFAULT_DURATION_MS = 4000;
const ERROR_DURATION_MS = 8000;

/** Phosphor's Info, Warning and X-Circle, as the gear button in the top bar is drawn. */
const icons: Record<Exclude<ToastKind, 'none'>, React.ReactElement> = {
  info: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" strokeWidth="16" />
      <circle cx="128" cy="84" r="12" fill="currentColor" />
      <path d="M116 116h20v64h-20z" fill="currentColor" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M128 36 20 212h216Z" fill="none" stroke="currentColor" strokeWidth="16" strokeLinejoin="round" />
      <path d="M118 104h20v56h-20z" fill="currentColor" />
      <circle cx="128" cy="184" r="12" fill="currentColor" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" strokeWidth="16" />
      <path d="M92 92l72 72M164 92l-72 72" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
    </svg>
  ),
};

/** A toast's message, changeable while it is up (`ToastHandle.update`): one tiny store per toast. */
class Live {
  private listeners = new Set<() => void>();
  constructor(public message: string) {}
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  get = () => this.message;
  set(message: string) {
    this.message = message;
    for (const l of this.listeners) l();
  }
}

const Content: React.FC<{ kind: ToastKind; live: Live; action?: ToastOptions['action']; onAction(): void }> = ({ kind, live, action, onAction }) => {
  const message = React.useSyncExternalStore(live.subscribe, live.get, live.get);
  return (
    <div className={`toast-inner toast-${kind}`}>
      {kind !== 'none' && <span className="toast-icon">{icons[kind]}</span>}
      <span className="toast-message">{message}</span>
      {action && (
        <button type="button" className="btn btn-primary toast-action" data-track="toast.action" onClick={onAction}>
          {action.label}
        </button>
      )}
    </div>
  );
};

export function setup(app: PiletApi) {
  const toast: Toast = {
    show(message: string, options: ToastOptions = {}): ToastHandle {
      const kind = options.kind ?? 'none';
      const autoClose = options.sticky ? undefined : (options.durationMs ?? (kind === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS));
      const live = new Live(message);
      // `close` is the host's; the action closes the toast after its work (2026-10-14).
      let close: () => void = () => undefined;
      const onAction = () => {
        options.action?.onClick();
        close();
      };
      close = app.showNotification(<Content kind={kind} live={live} action={options.action} onAction={onAction} />, { autoClose });
      return { update: (next) => live.set(next), close: () => close() };
    },
  };
  app.registerApi(TOAST_API, toast);
}
